"use client";

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { initialen } from "./formular/auswahl-konfiguration";

/**
 * Personen-Karte (PM Paket 3b, B2, Design-Guide `personen-karte.md`): kleine Karte zu EINER Person mit Name, Rolle im Projekt
 * und dem Zustand „inaktiv“. Zwei Auslöser, eine Karte:
 *  - `PersonenKarte` (Variante B, Katalog `contact-profile-popover-card`): ein Name im Text oder in einer Zeile öffnet die Karte
 *    per Klick/Tipp (Touch-tauglich), Tastatur (Enter/Leertaste) und schließt mit Escape oder Klick daneben.
 *  - `PersonenAvatarGruppe` (Variante A, Katalog `avatar-group-with-profile-popovers`): überlappende Avatare, jeder öffnet seine
 *    Karte per Klick/Tipp, per Fokus (Tastatur) und per Maus-Zeiger.
 *
 * Der Baustein bekommt fertige Angaben und kennt weder Quellen noch Rechte: der Aufrufer reicht nur Daten, die die Person ohnehin
 * sehen darf (r109/r110). Bewusst NICHT in der Karte (Betriebsrat, Datenschutz, Betreiber 11.10.2026): Auslastung je Person,
 * E-Mail, Fußaktionen des Katalogblocks (Flag, Kontakt hinzufügen). Abweichung AW-022.
 *
 * Die Karte hängt wie das Aufklappmenü am Dokument (`position: fixed`, randbewusst) und weicht nach oben aus, weil Karten und
 * Spalten des Bretts in scrollenden Containern liegen. Die Koordinaten setzt dieser Baustein zur Laufzeit (wie Preline-Popper).
 */
export interface PersonenKartenDaten {
  /** Anzeigename. */
  name: string;
  /** Rolle im Projekt („Projektleitung“, „Mitglied“), falls bekannt. */
  rolle?: string | null;
  /** `false` = Konto inaktiv (die Karte sagt „inaktiv“). */
  aktiv?: boolean;
}

const ABSTAND = 8;
const KARTE_FLAECHE = "fixed top-0 start-0 z-70 w-64 bg-popover border border-popover-line rounded-xl shadow-md text-start";
const AVATAR_GROSS = "flex shrink-0 justify-center items-center size-12 bg-layer border border-layer-line text-layer-foreground font-semibold uppercase rounded-full";

/** Inhalt der Karte (Katalog `contact-profile-popover-card`, ohne E-Mail und Fußaktionen). */
function KartenInhalt({ person }: { person: PersonenKartenDaten }) {
  const inaktiv = person.aktiv === false;
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-profile-popovers/contact-profile-popover-card */}
      <div className="p-4">
        <span className="block mb-2 text-sm text-muted-foreground-1">Person</span>
        <div className="flex flex-col justify-center items-center gap-y-2">
          <span className={AVATAR_GROSS} aria-hidden="true">
            {initialen(person.name)}
          </span>
          <p className="mt-2 font-medium text-foreground text-center break-words">{person.name}</p>
        </div>
        {(person.rolle || inaktiv) && (
          <div className="mt-1 text-center space-y-1">
            {person.rolle && <p className="text-sm text-muted-foreground-1">{person.rolle}</p>}
            {inaktiv && <p className="text-xs text-muted-foreground-1">inaktiv</p>}
          </div>
        )}
      </div>
    </>
  );
}

interface KartenSteuerung {
  offen: boolean;
  oeffne: () => void;
  schliesse: () => void;
  umschalten: () => void;
  ausloeser: React.RefObject<HTMLElement | null>;
  karte: React.RefObject<HTMLDivElement | null>;
  id: string;
}

/** Gemeinsamer Unterbau: Zustand, Platzieren am Auslöser, Schließen bei Escape, Klick daneben, Scrollen und Größenänderung. */
function useKarte(): KartenSteuerung {
  const [offen, setOffen] = useState(false);
  const ausloeser = useRef<HTMLElement | null>(null);
  const karte = useRef<HTMLDivElement | null>(null);
  const id = useId();

  const schliesse = useCallback(() => setOffen(false), []);
  const oeffne = useCallback(() => setOffen(true), []);
  const umschalten = useCallback(() => setOffen((o) => !o), []);

  useLayoutEffect(() => {
    if (!offen) return;
    const k = karte.current;
    const a = ausloeser.current;
    if (!k || !a) return;
    const auslB = a.getBoundingClientRect();
    const karteB = k.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    let links = auslB.left;
    let oben = auslB.bottom + ABSTAND;
    if (oben + karteB.height > vh - ABSTAND && auslB.top - ABSTAND - karteB.height > ABSTAND) oben = auslB.top - ABSTAND - karteB.height;
    links = Math.max(ABSTAND, Math.min(links, vw - karteB.width - ABSTAND));
    k.style.left = `${Math.round(links)}px`;
    k.style.top = `${Math.round(Math.max(ABSTAND, oben))}px`;
  });

  useEffect(() => {
    if (!offen) return;
    const beiKlick = (e: PointerEvent) => {
      const ziel = e.target as Node;
      if (karte.current?.contains(ziel) || ausloeser.current?.contains(ziel)) return;
      setOffen(false);
    };
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Escape schließt nur die Karte, nicht das Modal oder den Drawer darunter.
        e.stopPropagation();
        setOffen(false);
        ausloeser.current?.focus();
      }
    };
    const beiScroll = (e: Event) => {
      if (karte.current && e.target instanceof Node && karte.current.contains(e.target)) return;
      setOffen(false);
    };
    const beiGroesse = () => setOffen(false);
    document.addEventListener("pointerdown", beiKlick);
    document.addEventListener("keydown", beiTaste, true);
    document.addEventListener("scroll", beiScroll, true);
    window.addEventListener("resize", beiGroesse);
    return () => {
      document.removeEventListener("pointerdown", beiKlick);
      document.removeEventListener("keydown", beiTaste, true);
      document.removeEventListener("scroll", beiScroll, true);
      window.removeEventListener("resize", beiGroesse);
    };
  }, [offen]);

  return { offen, oeffne, schliesse, umschalten, ausloeser, karte, id };
}

function KartenFlaeche({ s, person, beiEintritt, beiAustritt }: { s: KartenSteuerung; person: PersonenKartenDaten; beiEintritt?: () => void; beiAustritt?: () => void }) {
  if (!s.offen || typeof document === "undefined") return null;
  return createPortal(
    <div
      ref={s.karte}
      id={`${s.id}-karte`}
      role="dialog"
      aria-label={`Person: ${person.name}`}
      className={KARTE_FLAECHE}
      onMouseEnter={beiEintritt}
      onMouseLeave={beiAustritt}
    >
      <KartenInhalt person={person} />
    </div>,
    document.body,
  );
}

/**
 * Variante B: ein Name als Auslöser (Klick/Tipp). `children` ersetzt den Namen als Beschriftung (z. B. Avatar plus Name);
 * `klasse` setzt die Schrift des umgebenden Textes fort.
 */
// Feste, vollständige Ketten je Größe (nie zusammengesetzt, R8).
const NAME_KLASSE = {
  sm: "inline-flex items-center max-w-full truncate text-start text-sm font-medium text-foreground underline-offset-2 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus rounded-xs cursor-pointer",
  xs: "inline-flex items-center max-w-full truncate text-start text-xs font-medium text-foreground underline-offset-2 hover:underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus rounded-xs cursor-pointer",
} as const;

export function PersonenKarte({
  person,
  children,
  groesse = "sm",
}: {
  person: PersonenKartenDaten;
  children?: React.ReactNode;
  /** Schrift des Namens: `sm` (Zeilen, Detail) oder `xs` (dichte Karten). */
  groesse?: "sm" | "xs";
}) {
  const s = useKarte();
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-profile-popovers/contact-profile-popover-card */}
      <button
        ref={(el) => {
          s.ausloeser.current = el;
        }}
        type="button"
        className={NAME_KLASSE[groesse]}
        aria-haspopup="dialog"
        aria-expanded={s.offen}
        aria-controls={s.offen ? `${s.id}-karte` : undefined}
        onClick={s.umschalten}
      >
        {children ?? person.name}
      </button>
      <KartenFlaeche s={s} person={person} />
    </>
  );
}

const AVATAR_KLEIN =
  "relative inline-flex justify-center items-center size-6 shrink-0 bg-layer border border-layer-line ring-2 ring-card rounded-full text-xs font-semibold uppercase text-layer-foreground hover:z-10 focus:outline-hidden focus-visible:z-10 focus-visible:ring-primary-focus cursor-pointer";

function AvatarMitKarte({ person }: { person: PersonenKartenDaten }) {
  const s = useKarte();
  const zeit = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stoppeZeit = () => {
    if (zeit.current) clearTimeout(zeit.current);
    zeit.current = null;
  };
  useEffect(() => stoppeZeit, []);
  const verzoegertSchliessen = () => {
    stoppeZeit();
    zeit.current = setTimeout(s.schliesse, 150);
  };
  return (
    <>
      <button
        ref={(el) => {
          s.ausloeser.current = el;
        }}
        type="button"
        className={AVATAR_KLEIN}
        aria-label={person.name}
        aria-haspopup="dialog"
        aria-expanded={s.offen}
        aria-controls={s.offen ? `${s.id}-karte` : undefined}
        onClick={s.umschalten}
        onPointerEnter={(e) => {
          // Maus-Zeiger öffnet; Touch öffnet nur per Tipp (onClick), damit der Tipp nicht doppelt zählt.
          if (e.pointerType === "touch" || e.pointerType === "pen") return;
          stoppeZeit();
          s.oeffne();
        }}
        onPointerLeave={(e) => {
          if (e.pointerType !== "touch" && e.pointerType !== "pen") verzoegertSchliessen();
        }}
        onFocus={(e) => {
          // Fokus per Tastatur öffnet; ein Klick fokussiert auch, darum nur bei :focus-visible.
          let sichtbar = false;
          try {
            sichtbar = e.currentTarget.matches(":focus-visible");
          } catch {
            sichtbar = false;
          }
          if (sichtbar) s.oeffne();
        }}
      >
        {initialen(person.name)}
      </button>
      <KartenFlaeche s={s} person={person} beiEintritt={stoppeZeit} beiAustritt={verzoegertSchliessen} />
    </>
  );
}

/**
 * Variante A: überlappende Avatare, jeder mit eigener Karte. Mehr als `max` Personen: die übrigen stehen als „+n“ (nur Zahl,
 * mit Text für Hilfstechnik); sie öffnen keine Karte, ihre Namen stehen dort, wo die Gruppe herkommt (Detail).
 */
export function PersonenAvatarGruppe({ personen, beschriftung, max = 3 }: { personen: PersonenKartenDaten[]; beschriftung: string; max?: number }) {
  if (personen.length === 0) return null;
  const sichtbar = personen.slice(0, max);
  const rest = personen.length - sichtbar.length;
  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-profile-popovers/avatar-group-with-profile-popovers */}
      <div className="flex -space-x-1 items-center" role="group" aria-label={beschriftung}>
        {sichtbar.map((p, i) => (
          <AvatarMitKarte key={`${p.name}-${i}`} person={p} />
        ))}
        {rest > 0 && (
          <span className="ps-2 text-xs text-muted-foreground-1">
            <span aria-hidden="true">+{rest}</span>
            <span className="sr-only">und {rest} weitere</span>
          </span>
        )}
      </div>
    </>
  );
}
